import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, ShieldCheck, Sun, Heart, Droplets, Phone } from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] }
};

const TIMELINE = [
  {
    period: 'Weeks 1-2',
    title: 'The Wound',
    description: 'Red, firm, new. Still the wound\'s voice, not yet the scar.'
  },
  {
    period: 'Weeks 2-6',
    title: 'The Building',
    description: 'The scar speaks loudest here. Raised, pink, easily marked by sun or touch.'
  },
  {
    period: 'Months 2-12',
    title: 'The Quietening',
    description: 'It quietens. Colour drains toward the skin beside it. The line softens underhand.'
  },
  {
    period: 'Beyond a Year',
    title: 'The Settling',
    description: 'Close to its final shape. Pale, flat, almost silent. Most scars keep refining in small ways long after.'
  }
];

const CARE_STEPS = [
  {
    icon: ShieldCheck,
    title: 'Keep it clean',
    text: 'Keep the wound clean and dry until fully healed. You will be guided in your dressing clinic appointments.'
  },
  {
    icon: Heart,
    title: 'Gentle massage',
    text: 'Once the wound is closed, begin gentle massage. Usually at 3 weeks from surgery. Firm circular pressure with a plain moisturiser, a few minutes, morning and night.'
  },
  {
    icon: Sun,
    title: 'Shield from sun',
    text: 'Shield the scar from the sun completely for the first year. New scar tissue burns and darkens far more easily than the skin around it.'
  },
  {
    icon: Droplets,
    title: 'Silicone therapy',
    text: 'Use silicone gel or sheets daily for at least twelve weeks. This is the single most effective thing you can do for the final appearance.'
  },
  {
    icon: Clock,
    title: 'Support healing',
    text: 'Avoid smoking and limit alcohol in the early weeks, both slow healing. Eat well and stay hydrated. Healing is quietly hard work for the body.'
  }
];

export default function ScarGuidePage() {
  return (
    <div className="bg-clinic-bg" data-testid="scar-guide-page">
      <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="scar-guide-hero">
        <div className="absolute inset-0 z-0">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover"
          >
            <source src="/scar-guide-video.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-clinic-bg/30" />
        </div>

        <div className="container-clinic relative z-10 py-32 md:py-40">
          <motion.div
            initial="initial"
            animate="animate"
            variants={{ animate: { transition: { staggerChildren: 0.1 } } }}
            className="max-w-3xl"
          >
            <motion.p variants={fadeInUp} className="text-caption mb-4">Patient Resource</motion.p>
            <motion.h1 variants={fadeInUp} className="text-heading-1 text-clinic-charcoal mb-8">
              A Guide to Your Scar
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-body-lg">
              A cut on the skin does not heal back. It heals over. Where the body is opened, 
              it closes the gap with something new &mdash; not the skin you had, but a tissue built 
              quickly for strength over beauty. That is the scar.
            </motion.p>
            <motion.p variants={fadeInUp} className="text-body-lg mt-4">
              We do not ask a scar to disappear. We ask it to settle, to fade, to stop asking for attention.
            </motion.p>
          </motion.div>
        </div>
      </section>

      <section className="section-padding bg-white" data-testid="scar-timeline">
        <div className="container-clinic">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <p className="text-caption mb-4">Healing Timeline</p>
            <h2 className="text-heading-2 text-clinic-charcoal">What to expect at each stage</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {TIMELINE.map((stage, index) => (
              <motion.div
                key={stage.period}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="relative"
                data-testid={`timeline-stage-${index}`}
              >
                <span className="text-5xl font-heading text-clinic-border mb-4 block">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <p className="text-caption mb-2">{stage.period}</p>
                <h3 className="text-heading-3 text-clinic-charcoal mb-3">{stage.title}</h3>
                <p className="text-body-lg text-sm">{stage.description}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mt-16 p-8 bg-clinic-bg border border-clinic-border max-w-3xl"
          >
            <p className="text-body-lg italic">
              &ldquo;Then, the building. Weeks of quiet labour beneath the surface, new tissue knitting 
              across the wound. The scar is loudest here, pink and firm, most itself. Then, the long quiet. 
              Months of softening, fading, settling. The scar stops speaking and becomes simply skin again.&rdquo;
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section-padding" data-testid="scar-care">
        <div className="container-clinic">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mb-16"
          >
            <p className="text-caption mb-4">Aftercare</p>
            <h2 className="text-heading-2 text-clinic-charcoal mb-6">How to Care for It</h2>
            <p className="text-body-lg">
              Simple habits, done consistently, shape how a scar finally looks.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {CARE_STEPS.map((step, index) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.08 }}
                className="p-8 bg-white border border-clinic-border"
                data-testid={`care-step-${index}`}
              >
                <div className="w-12 h-12 flex items-center justify-center border border-clinic-border mb-6">
                  <step.icon size={20} className="text-clinic-charcoal" />
                </div>
                <h3 className="text-heading-3 text-clinic-charcoal mb-3">{step.title}</h3>
                <p className="text-body-lg text-sm">{step.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-clinic-muted" data-testid="scar-when-to-call">
        <div className="container-clinic">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="w-14 h-14 flex items-center justify-center border border-clinic-border bg-white mb-8">
                <Phone size={22} className="text-clinic-charcoal" />
              </div>
              <p className="text-caption mb-4">Important</p>
              <h2 className="text-heading-2 text-clinic-charcoal mb-6">When to Call</h2>
              <p className="text-body-lg mb-8">
                Please contact the clinic promptly if you notice:
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-clinic-charcoal mt-2.5 flex-shrink-0" />
                  <span className="text-body-lg">Discharge, odour, or a wound reopening</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-clinic-charcoal mt-2.5 flex-shrink-0" />
                  <span className="text-body-lg">A scar that thickens or raises after the early months</span>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="flex items-center"
            >
              <div className="p-10 bg-white border border-clinic-border w-full">
                <p className="text-body-lg mb-6 italic">
                  Every scar tells time differently. Healing is rarely a straight line, 
                  and no two patients heal quite alike. If anything here concerns you, 
                  or simply does not match what you are seeing, we are only a call away.
                </p>
                <Link to="/contact" className="btn-primary" data-testid="scar-guide-contact-btn">
                  Contact the Clinic
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
